import type { ErrorEvent, EventHint } from '@sentry/react';
import { isJwtExpiredError } from '@/services/auth/sessionCache';

/**
 * Messaggi (substring) di errori benigni che non devono raggiungere Sentry:
 * rumore già gestito gracefully dall'app, non azionabile per il triage.
 */
const DROPPED_MESSAGE_SUBSTRINGS = [
  // Fallimenti di rete generici (offline, richiesta annullata): non un bug.
  'Failed to fetch',
  // Plugin App non implementato su alcuni device Android: c'è fallback popstate.
  'App" plugin is not implemented',
];

/**
 * Filtro `beforeSend` di Sentry, estratto come funzione pura per poterlo testare
 * senza inizializzare Sentry. Ritorna l'evento per inviarlo, oppure `null` per
 * scartarlo.
 *
 * Scarta anche i "JWT expired" / PGRST303: una sessione scaduta è benigna perché
 * gestita dal refresh silenzioso (`autoRefreshToken` sul client Supabase e
 * `ensureValidSession`), non un errore da mostrare nel triage. (Issue #1461)
 */
export function filterSentryEvent(
  event: ErrorEvent,
  hint?: EventHint,
): ErrorEvent | null {
  const original = hint?.originalException;

  if (
    original instanceof Error &&
    typeof original.message === 'string' &&
    DROPPED_MESSAGE_SUBSTRINGS.some((substring) => original.message.includes(substring))
  ) {
    return null;
  }

  if (isJwtExpiredError(original)) {
    return null;
  }

  return event;
}
